import { useState } from "react"
import { Link } from "react-router-dom"
import { useBranding } from "@/config/context"
import { useAuth } from "@/state/AuthContext"
import { useNostr } from "@/state/NostrContext"
import { shortNpub } from "@/utils/nip19"
import { Avatar } from "./Avatar"
import { Icon } from "./Icon"
import { LoginDialog } from "./LoginDialog"
import { Modal } from "./Modal"
import { useProfile } from "@/hooks/useProfiles"

export interface AccountMenuProps {
  open: boolean
  onClose: () => void
}

/** Account sheet: identity, account switching, relay status and sign out. */
export function AccountMenu({ open, onClose }: AccountMenuProps) {
  const branding = useBranding()
  const { pubkey, method, accounts, canSign, logout, forgetAccount, switchAccount } = useAuth()
  const { statuses } = useNostr()
  const profile = useProfile(pubkey)
  const [loginOpen, setLoginOpen] = useState(false)

  const connected = statuses.filter((status) => status.state === "connected").length

  return (
    <>
      <Modal open={open} onClose={onClose} title="Account" size="small">
        {pubkey ? (
          <div className="account-summary">
            <Avatar src={profile?.picture} label={profile?.displayName || pubkey} size={48} />
            <div>
              <p className="account-name">{profile?.displayName || profile?.name || shortNpub(pubkey)}</p>
              <p className="muted small">{shortNpub(pubkey)}</p>
              <p className="badge-row">
                <span className={`badge ${canSign ? "badge-primary" : ""}`}>
                  {canSign ? `${method} signer` : "read-only"}
                </span>
              </p>
            </div>
          </div>
        ) : (
          <p className="muted">You are browsing anonymously. Relay feeds and exploration work without an account.</p>
        )}

        <div className="account-actions">
          {pubkey && (
            <Link to="/profile" className="btn btn-ghost btn-block" onClick={onClose}>
              <Icon name="user" size={16} /> Profile
            </Link>
          )}
          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={() => {
              if (pubkey) logout()
              else setLoginOpen(true)
              onClose()
            }}
          >
            <Icon name={pubkey ? "logout" : "user"} size={16} />
            {pubkey ? "Log out" : "Log in"}
          </button>
          <Link to="/settings" className="btn btn-ghost btn-block" onClick={onClose}>
            <Icon name="settings" size={16} /> Settings
          </Link>
          <Link to="/settings#relays" className="btn btn-ghost btn-block" onClick={onClose}>
            <Icon name="relay" size={16} /> Manage relays
          </Link>
        </div>

        <div className="account-relays">
          <h3 className="section-label">Relay connections</h3>
          <p className="muted small">
            {connected} of {statuses.length} relays connected
          </p>
          <ul className="account-relay-list">
            {statuses.slice(0, 8).map((status) => (
              <li key={status.url} className="relay-list-item">
                <span className={`connection-dot connection-${status.state === "connected" ? "ok" : "bad"}`} />
                <span className="relay-list-url">{status.url.replace(/^wss?:\/\//, "")}</span>
              </li>
            ))}
          </ul>
        </div>

        {accounts.length > 0 && (
          <div className="account-list">
            <h3 className="section-label">Saved accounts</h3>
            {accounts.map((account) => (
              <div key={account.pubkey} className="account-row-wrap">
                <button
                  type="button"
                  className={`account-row ${account.pubkey === pubkey ? "active" : ""}`}
                  onClick={async () => {
                    await switchAccount(account.pubkey)
                    onClose()
                  }}
                >
                  <span>{account.pubkey.slice(0, 16)}…</span>
                  <span className="badge">{account.method}</span>
                </button>
                <button
                  type="button"
                  className="icon-button small"
                  aria-label="Forget this account"
                  onClick={() => forgetAccount(account.pubkey)}
                >
                  <Icon name="x" size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        <p className="muted small account-footer">
          {branding.name} · {branding.description}
        </p>
      </Modal>
      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
    </>
  )
}
